import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { MoreVertical } from 'lucide-react'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import type { RUserSession } from 'v2/types/api/auth'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { IntegrationState } from '../utils/constants'
import type { ExternalAppRow } from './external-app-actions'

export type AccountMenuAction =
  | 'share'
  | 'revoke'
  | 'custom-fields'
  | 'reconnect'
  | 'enable'
  | 'disable'
  | 'delete'

interface AccountMenuEligibility {
  state?: string
  isOwner: boolean
  isShared?: boolean
  customFieldsEnabled?: boolean
  canEdit: boolean
  canDelete: boolean
}

export const getAccountMenuActions = ({
  state,
  isOwner,
  isShared = false,
  customFieldsEnabled = false,
  canEdit,
  canDelete,
}: AccountMenuEligibility): AccountMenuAction[] => {
  // Only the person who connected the account may change or share it.
  if (!isOwner) return []

  const actions: AccountMenuAction[] = ['share']
  if (isShared) actions.push('revoke')
  if (customFieldsEnabled && canEdit) actions.push('custom-fields')
  const normalized = state?.toUpperCase()
  // Reconnect is per row: with several accounts, one sheet-level button could not name which.
  if (normalized === IntegrationState.EXPIRED && canEdit) actions.push('reconnect')
  if (canEdit) {
    if (normalized === IntegrationState.DISABLED) actions.push('enable')
    else if (normalized === IntegrationState.ACTIVE) actions.push('disable')
  }
  if (canDelete) actions.push('delete')
  return actions
}

export const isConnectionShared = (connection: ExternalAppRow) =>
  Boolean(connection.shared_with_org) ||
  (connection.users_with_access ?? []).some((user) => user.id !== connection.integrated_by?.id)

const LABELS: Record<AccountMenuAction, string> = {
  share: 'Share',
  revoke: 'Revoke access',
  'custom-fields': 'Custom fields',
  reconnect: 'Reconnect',
  enable: 'Enable',
  disable: 'Disable',
  delete: 'Delete',
}

interface AccountActionsMenuProps {
  connection: ExternalAppRow
  currentUser?: RUserSession
  onAction: (action: AccountMenuAction, connection: ExternalAppRow) => void
}

export const AccountActionsMenu = ({
  connection,
  currentUser,
  onAction,
}: AccountActionsMenuProps) => {
  const { hasPermission } = usePermissions()
  const actions = getAccountMenuActions({
    state: connection.state,
    isOwner: currentUser?.aom_uuid === connection.integrated_by?.id,
    isShared: isConnectionShared(connection),
    customFieldsEnabled: Boolean(connection.is_custom_fields_enabled),
    canEdit: hasPermission(RbacModules.integration, RbacPermissionTypes.edit),
    canDelete: hasPermission(RbacModules.integration, RbacPermissionTypes.delete),
  })

  if (actions.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="size-8 shrink-0 p-0 hover:bg-hv1"
          data-testid="button-account-actions"
          aria-label={`Actions for ${connection.name}`}
        >
          <MoreVertical className="size-4 text-secondary" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action}
            className={cn(
              'text-s-400',
              (action === 'delete' || action === 'revoke') &&
                'text-destructive focus:text-destructive',
            )}
            onSelect={() => onAction(action, connection)}
            data-testid={`menu-item-${action}-account`}
          >
            {LABELS[action]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
